import * as React from "react";
import { BrandIcMinus, BrandIcPlus } from "@racoon/icons";
import { FieldShell } from "./field-shell";

const formatValue = (value: number | undefined) =>
  value == null || Number.isNaN(value) ? "" : String(Math.round(value * 100) / 100);

const parseValue = (raw: string): number | undefined => {
  const normalized = raw.trim().replace(",", ".");
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

/**
 * Local draft for a numeric TTN field: typed text is kept as-is (so "3," or "0."
 * survive mid-edit) and parsed once on blur / Enter. An empty or invalid draft
 * reverts to `value`; `onCommit` only fires when the number actually changed.
 */
const useNumberDraft = (value: number | undefined, onCommit: (value: number) => void) => {
  const [draft, setDraft] = React.useState(formatValue(value));
  React.useEffect(() => setDraft(formatValue(value)), [value]);

  const commit = () => {
    // Untouched: the text is `value` rounded for display, so parsing it back
    // would read as a change (12.3456 → "12.35") on a mere focus and blur.
    if (draft === formatValue(value)) return;
    const parsed = parseValue(draft);
    if (parsed === undefined) setDraft(formatValue(value));
    else if (parsed !== value) onCommit(parsed);
  };

  return {
    value: draft,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => setDraft(event.target.value),
    onBlur: commit,
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") event.currentTarget.blur();
    },
  };
};

type Props = {
  value: number | undefined;
  /** Called on blur / Enter, only when the parsed number differs from `value`. */
  onCommit: (value: number) => void;
  unit?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  title?: string;
};

/** Numeric consignment-note field with an optional unit suffix. */
export const ConsignmentNumberInput: React.FC<Props> = ({
  value,
  onCommit,
  unit,
  placeholder,
  disabled,
  className,
  title,
}) => {
  const inputProps = useNumberDraft(value, onCommit);

  return (
    <FieldShell className={className} disabled={disabled} title={title}>
      <input
        inputMode="decimal"
        disabled={disabled}
        placeholder={placeholder}
        {...inputProps}
        className="min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-secondary disabled:cursor-not-allowed"
      />
      {unit && <span className="shrink-0 text-xs text-text-secondary">{unit}</span>}
    </FieldShell>
  );
};

/**
 * "− 3 +" stepper (Figma: Raccoon-AI_Desktop 21344:42608). The buttons move by
 * whole steps; unless `readOnly`, the value in the middle takes any float typed by
 * hand. `onIncrement`/`onDecrement` replace the buttons' default step.
 */
export const ConsignmentStepper: React.FC<{
  value: number | undefined;
  onCommit: (value: number) => void;
  onIncrement?: () => void;
  onDecrement?: () => void;
  canDecrement?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  "aria-label"?: string;
}> = ({
  value,
  onCommit,
  onIncrement,
  onDecrement,
  canDecrement,
  disabled,
  readOnly,
  "aria-label": ariaLabel,
}) => {
  const inputProps = useNumberDraft(value, onCommit);
  const current = value ?? 0;

  return (
    <div className="flex items-center justify-center gap-1">
      <button
        type="button"
        aria-label="−1"
        disabled={disabled || !(canDecrement ?? current > 0)}
        onClick={onDecrement ?? (() => onCommit(Math.max(0, current - 1)))}
        className="size-6 shrink-0 text-icon-action disabled:opacity-40"
      >
        <BrandIcMinus className="size-full" />
      </button>
      <input
        inputMode="decimal"
        aria-label={ariaLabel}
        disabled={disabled}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        placeholder="0"
        {...inputProps}
        className={`w-10 min-w-0 rounded bg-transparent text-center text-sm leading-[14px] tracking-[-0.14px] text-text-body outline-none ${
          readOnly ? "cursor-default" : "focus:ring-1 focus:ring-brand-blue"
        }`}
      />
      <button
        type="button"
        aria-label="+1"
        disabled={disabled}
        onClick={onIncrement ?? (() => onCommit(current + 1))}
        className="size-6 shrink-0 text-icon-action disabled:opacity-40"
      >
        <BrandIcPlus className="size-full" />
      </button>
    </div>
  );
};
